import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowRight, ArrowLeft, Loader2, Trophy, RotateCcw, BadgeCheck, BookmarkCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const QUESTIONS = [
    {
        key: "audience",
        label: "Vous êtes…",
        options: [
            { value: "b2c", label: "Particulier", help: "Foyer, appartement, maison" },
            { value: "b2b", label: "Professionnel", help: "TPE, PME, ETI, collectivité" },
        ],
        type: "radio",
    },
    {
        key: "energy_type",
        label: "Quelle énergie vous intéresse ?",
        options: [
            { value: "electricity", label: "Électricité seule" },
            { value: "gas", label: "Gaz seul" },
            { value: "both", label: "Électricité + gaz" },
        ],
        type: "radio",
    },
    {
        key: "priority",
        label: "Qu'est-ce qui compte le plus pour vous ?",
        options: [
            { value: "price", label: "Le prix le plus bas" },
            { value: "green", label: "Énergie 100% verte" },
            { value: "service", label: "Service client de qualité" },
            { value: "digital", label: "Outils digitaux modernes" },
        ],
        type: "radio",
    },
];

const ADVANCED = [
    {
        key: "green_preference",
        label: "Sensibilité écologique",
        options: [
            { value: "low", label: "Faible" },
            { value: "medium", label: "Moyenne" },
            { value: "high", label: "Forte" },
        ],
    },
    {
        key: "budget",
        label: "Budget",
        options: [
            { value: "low", label: "Économique" },
            { value: "standard", label: "Standard" },
            { value: "premium", label: "Premium" },
        ],
    },
];

const DEFAULT_PROFILE = {
    audience: "b2c",
    energy_type: "electricity",
    priority: "price",
    green_preference: "medium",
    budget: "standard",
};

export default function IdealProfileQuiz() {
    const { user } = useAuth();
    const [step, setStep] = useState(0);
    const [profile, setProfile] = useState(DEFAULT_PROFILE);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

    const next = () => setStep((s) => Math.min(s + 1, QUESTIONS.length));
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const submit = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/suppliers/recommend", profile);
            setResult(data);
            if (data.saved_id) {
                toast.success("Recommandations prêtes et sauvegardées dans votre espace");
            } else {
                toast.success("Vos recommandations sont prêtes");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setProfile(DEFAULT_PROFILE);
        setStep(0);
        setResult(null);
        setShowAdvanced(false);
    };

    // RESULT VIEW
    if (result) {
        return (
            <Card className="rounded-3xl border-border bg-primary text-primary-foreground overflow-hidden" data-testid="quiz-result-card">
                <CardContent className="p-7 md:p-10">
                    <div className="flex items-start justify-between gap-4 mb-6">
                        <div>
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Recommandations</p>
                            <h3 className="font-display text-3xl md:text-4xl font-black mt-2">Votre top 3 fournisseurs.</h3>
                            <p className="text-primary-foreground/80 mt-2 text-sm">
                                Analyse basée sur {result.eligible_count || 0} fournisseurs éligibles.
                            </p>
                            {result.saved_id && (
                                <p className="text-xs text-accent mt-2 flex items-center gap-1.5" data-testid="quiz-saved-badge">
                                    <BookmarkCheck className="h-3.5 w-3.5" />
                                    Sauvegardé dans votre espace client
                                </p>
                            )}
                            {!user && (
                                <p className="text-xs text-primary-foreground/60 mt-2">
                                    <Link to="/connexion" className="underline hover:text-accent" data-testid="quiz-login-cta">Connectez-vous</Link> pour sauvegarder vos recommandations.
                                </p>
                            )}
                        </div>
                        <Button onClick={reset} variant="ghost" size="sm" type="button" className="text-primary-foreground hover:bg-primary-foreground/10 rounded-full" data-testid="quiz-reset">
                            <RotateCcw className="h-4 w-4 mr-2" />Recommencer
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="quiz-recommendations">
                        {(result.top || []).map((entry, idx) => (
                            <div
                                key={entry.supplier_id}
                                className={`rounded-2xl p-5 ${idx === 0 ? "bg-accent text-accent-foreground" : "bg-primary-foreground/10 text-primary-foreground"}`}
                                data-testid={`quiz-rec-${entry.supplier_id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {idx === 0 ? <Trophy className="h-5 w-5" /> : <BadgeCheck className="h-5 w-5" />}
                                        <span className="text-xs uppercase tracking-wider font-bold">
                                            #{idx + 1} {idx === 0 ? "Top match" : ""}
                                        </span>
                                    </div>
                                    <span className="font-mono font-black text-xl">{entry.match_score ?? "?"}%</span>
                                </div>
                                <h4 className="font-display text-2xl font-black mt-3">{entry.supplier_name || entry.supplier_id}</h4>
                                {entry.tagline && (
                                    <p className={`text-xs italic mt-1 ${idx === 0 ? "text-accent-foreground/70" : "text-primary-foreground/60"}`}>
                                        "{entry.tagline}"
                                    </p>
                                )}
                                <p className={`text-sm mt-3 leading-relaxed ${idx === 0 ? "text-accent-foreground/90" : "text-primary-foreground/85"}`}>
                                    {entry.reason}
                                </p>
                                <Button
                                    asChild
                                    size="sm"
                                    variant={idx === 0 ? "default" : "outline"}
                                    className={`rounded-full mt-4 ${
                                        idx === 0
                                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                            : "border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
                                    }`}
                                    data-testid={`quiz-rec-cta-${entry.supplier_id}`}
                                >
                                    <Link to={`/fournisseurs/${entry.supplier_id}`}>Voir la fiche <ArrowRight className="h-4 w-4 ml-2" /></Link>
                                </Button>
                            </div>
                        ))}
                    </div>

                    {result.advice && (
                        <div className="mt-6 bg-primary-foreground/10 rounded-2xl p-5" data-testid="quiz-advice">
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-2">Conseil Energy Market</p>
                            <p className="text-sm leading-relaxed text-primary-foreground/90">{result.advice}</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    const currentQuestion = QUESTIONS[step];
    const isLast = step === QUESTIONS.length - 1;

    return (
        <Card className="rounded-3xl border-border bg-card overflow-hidden" data-testid="quiz-card">
            <CardContent className="p-7 md:p-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-7">
                    <div>
                        <p className="em-eyebrow flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            Profil idéal · Recommandation
                        </p>
                        <h3 className="font-display text-3xl md:text-4xl font-black text-primary mt-2 tracking-tight">
                            Trouvez votre fournisseur en 3 questions.
                        </h3>
                        <p className="text-muted-foreground mt-2 max-w-2xl">
                            Notre moteur d'analyse propose le top 3 des fournisseurs adaptés à votre profil.
                        </p>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Question <span className="font-bold text-primary">{step + 1}</span> / {QUESTIONS.length}
                    </div>
                </div>

                {/* PROGRESS BAR */}
                <div className="grid gap-2 mb-7" style={{ gridTemplateColumns: `repeat(${QUESTIONS.length}, 1fr)` }}>
                    {QUESTIONS.map((q, idx) => (
                        <div
                            key={q.key}
                            className={`h-1.5 rounded-full transition-colors ${idx <= step ? "bg-primary" : "bg-secondary"}`}
                        />
                    ))}
                </div>

                {/* QUESTION */}
                <div data-testid={`quiz-step-${step}`}>
                    <Label className="text-lg md:text-xl font-display font-bold text-primary">
                        {currentQuestion.label}
                    </Label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                        {currentQuestion.options.map((opt) => {
                            const selected = profile[currentQuestion.key] === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => update(currentQuestion.key, opt.value)}
                                    data-testid={`quiz-option-${currentQuestion.key}-${opt.value}`}
                                    className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                        selected
                                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                            : "border-border bg-card hover:border-primary/40"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-display font-bold text-base">{opt.label}</span>
                                        {selected && <BadgeCheck className="h-5 w-5 text-primary" />}
                                    </div>
                                    {opt.help && <p className="text-xs text-muted-foreground mt-1">{opt.help}</p>}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ADVANCED (last step only) */}
                {isLast && (
                    <div className="mt-6 pt-6 border-t border-border" data-testid="quiz-advanced">
                        <button
                            type="button"
                            onClick={() => setShowAdvanced((v) => !v)}
                            className="text-sm font-semibold text-primary hover:underline"
                            data-testid="quiz-advanced-toggle"
                        >
                            {showAdvanced ? "Masquer" : "Affiner"} les critères avancés (optionnel)
                        </button>
                        {showAdvanced && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                {ADVANCED.map((field) => (
                                    <div key={field.key}>
                                        <Label className="em-eyebrow">{field.label}</Label>
                                        <Tabs value={profile[field.key]} onValueChange={(v) => update(field.key, v)} className="mt-2">
                                            <TabsList className="w-full rounded-full">
                                                {field.options.map((opt) => (
                                                    <TabsTrigger
                                                        key={opt.value}
                                                        value={opt.value}
                                                        className="flex-1 rounded-full"
                                                        data-testid={`quiz-advanced-${field.key}-${opt.value}`}
                                                    >
                                                        {opt.label}
                                                    </TabsTrigger>
                                                ))}
                                            </TabsList>
                                        </Tabs>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* NAV */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                    <Button
                        onClick={prev}
                        disabled={step === 0}
                        variant="ghost"
                        type="button"
                        className="rounded-full"
                        data-testid="quiz-prev"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" /> Précédent
                    </Button>
                    {!isLast ? (
                        <Button
                            onClick={next}
                            type="button"
                            className="rounded-full bg-primary hover:bg-primary/90"
                            data-testid="quiz-next"
                        >
                            Suivant <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    ) : (
                        <Button
                            onClick={submit}
                            disabled={loading}
                            type="button"
                            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                            data-testid="quiz-submit"
                        >
                            {loading ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse…</>
                            ) : (
                                <><Sparkles className="h-4 w-4 mr-2" />Obtenir mes recommandations</>
                            )}
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
